import { useState, ReactNode, ReactElement } from 'react';
import { jwtDecode } from 'jwt-decode';
import {
    Tokens,
    DecodedTokens,
    JWTRefreshPayload,
    JWTAccessPayload,
    RawTokens
} from './tokens';
import { NullableTokensContext } from './context';

const ACCESS_LOCAL_STORAGE_KEY = 'accessToken';
const REFRESH_LOCAL_STORAGE_KEY = 'refreshToken';

type Props = {
    readonly children?: ReactNode;
};
export function NullableTokensProvider({ children }: Props): ReactElement {
    const [tokens, _setTokens] = useState<Tokens | null>(() => {
        const accessToken = localStorage.getItem(ACCESS_LOCAL_STORAGE_KEY);
        const refreshToken = localStorage.getItem(REFRESH_LOCAL_STORAGE_KEY);
        if (accessToken && refreshToken) {
            const decoded = decodeTokens({ accessToken, refreshToken });
            if (decoded) {
                return {
                    accessToken,
                    accessTokenPayload: decoded.accessToken,
                    refreshToken,
                    refreshTokenPayload: decoded.refreshToken
                };
            } else {
                return null;
            }
        } else {
            return null;
        }
    });

    const setTokens = (options: {
        accessToken: string;
        refreshToken: string;
    }): void => {
        const { accessToken, refreshToken } = options;

        const decoded = decodeTokens({ accessToken, refreshToken });
        if (decoded) {
            localStorage.setItem(ACCESS_LOCAL_STORAGE_KEY, accessToken);
            localStorage.setItem(REFRESH_LOCAL_STORAGE_KEY, refreshToken);

            _setTokens({
                accessToken,
                accessTokenPayload: decoded.accessToken,
                refreshToken,
                refreshTokenPayload: decoded.refreshToken
            });
        } else {
            _setTokens(null);
        }
    };

    const setAccessToken = (accessToken: string): void => {
        if (!tokens) {
            return;
        }
        setTokens({ accessToken, refreshToken: tokens.refreshToken });
    };

    const deleteTokens = (): void => {
        localStorage.removeItem(ACCESS_LOCAL_STORAGE_KEY);
        localStorage.removeItem(REFRESH_LOCAL_STORAGE_KEY);
        _setTokens(null);
    };

    return (
        <NullableTokensContext.Provider
            value={{
                tokens,
                setTokens,
                setAccessToken,
                deleteTokens
            }}
        >
            {children}
        </NullableTokensContext.Provider>
    );
}

/**
 * Helper functions
 */
function decodeTokens(tokens: RawTokens): DecodedTokens | null {
    // Decode refresh token

    let refreshDecoded: JWTRefreshPayload;
    try {
        refreshDecoded = jwtDecode(tokens.refreshToken);
    } catch {
        return null;
    }

    // Decode access token
    let accessDecoded: JWTAccessPayload;
    try {
        accessDecoded = jwtDecode(tokens.accessToken);
    } catch {
        return null;
    }
    return {
        accessToken: accessDecoded,
        refreshToken: refreshDecoded
    };
}
